import React from 'react';
import { Delete, DragHandle } from '@mui/icons-material';
import { Checkbox, FormControlLabel, InputLabel, MenuItem, Select } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Controller, useFormContext } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import { LayoutType, PageListItemBody, PageListUiModuleBody, UiModulePageListTreatment } from '../../../../../API';
import { useLocales, useTheme } from '../../../../../hooks';
import FormControl from '../../../../shared/FormControl';
import IconButton from '../../../../shared/IconButton';
import InputController from '../../../../shared/InputController';
import LocalizedInputCollection from '../../../../shared/LocalizedInputCollection';
import TextValidator from '../../../../shared/TextValidator';
import { useData } from '../../../../../data-layer';
import { AssetTypes, AssetTypesEnum } from '../../../../../utils/assetTypes';
import { markAsRequired } from '../../../../../utils/formHelpers';
import { FormField } from '../../styles';
import AssetBrowser from '../../../../Assets/AssetBrowser';
import { pageListContentItemTestIds } from '../../../../shared/TestsIds';
import { LokaliseAutocomplete } from '../../../../shared/LokaliseAutocomplete';

interface IPageListContentItemProps {
  index: number;
  onDelete: () => void;
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex'
  },
  rowLeft: {
    flexGrow: 1
  },
  rowRight: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  imageFormField: {
    width: '100%'
  },
  dragHandle: {
    cursor: 'grab',
    color: theme.palette.action.active,
    userSelect: 'none',
    '&:active': {
      cursor: 'grabbing'
    }
  }
}));

function PageListContentItem({ index, onDelete }: IPageListContentItemProps): JSX.Element {
  const { control, watch, getValues, reset } = useFormContext<PageListUiModuleBody>();

  const moduleId = watch('moduleId');
  const treatment = watch('pageListTreatment');

  const { t } = useLocales();
  const { formControlColor } = useTheme();
  const { classes } = useStyles();

  const {
    pages: {
      state: { withRecordBucket: withPagesByType }
    }
  } = useData();

  const pages = useRecoilValue(withPagesByType(LayoutType.PAGE));

  const AssetPicker = ({
    name,
    assetType,
    required = false
  }: {
    name: keyof PageListItemBody;
    assetType: AssetTypesEnum;
    required?: boolean;
  }): JSX.Element => {
    const isRequired = (label: string) => (required ? markAsRequired(label) : label);
    return (
      <Controller
        control={control}
        name={`pages.${index}.${name}`}
        rules={{ required }}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <FormField className={classes.imageFormField}>
              <AssetBrowser
                assetId={moduleId || ''}
                assetType={assetType}
                value={value as string}
                data-testid={pageListContentItemTestIds.formField(name)}
                hasError={!!error}
                onChange={onChange}
                label={isRequired(t(`layouts.page_list.fields.${name}`))}
              />
            </FormField>
          );
        }}
      />
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.rowLeft}>
        {treatment === UiModulePageListTreatment.CLIPS_VERTICAL_TILES && (
          <FormField>
            <Controller
              control={control}
              name={`pages.${index}.isClipsDestination`}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  label={t('layouts.page_list.fields.isClipsDestination')}
                  control={
                    <Checkbox
                      name={'isClipsDestination'}
                      checked={value as boolean}
                      color={formControlColor}
                      onChange={onChange}
                      data-testid={pageListContentItemTestIds.isClipDestinationCheckbox}
                    />
                  }
                />
              )}
            />
          </FormField>
        )}
        <div />
        <FormField>
          <LocalizedInputCollection
            getValues={getValues}
            reset={reset}
            fields={[
              {
                component: (
                  <InputController
                    control={control}
                    name={`pages.${index}.name`}
                    render={({ field: { value, onChange } }) => (
                      <TextValidator
                        name="name"
                        value={value}
                        onChange={onChange}
                        label={t('general.name')}
                        color={formControlColor}
                        fullWidth
                        validators={['required']}
                        errorMessages={[t('general.field_is_required')]}
                      />
                    )}
                  />
                )
              }
            ]}
          />
        </FormField>
        <FormField>
          <FormControl fullWidth>
            <InputLabel id="page-select-label">{t('layouts.page')}</InputLabel>
            <Select
              label={t('layouts.page')}
              labelId="page-select-label"
              value={getValues(`pages.${index}.urlPath`)}
              data-testid={pageListContentItemTestIds.pagePathSelector}
              onChange={({ target: { value } }) => {
                const pageModule = getValues();
                const pages = pageModule.pages.slice();
                pages[index] = { ...pages[index], urlPath: value };
                reset({
                  ...pageModule,
                  pages
                });
              }}
              disabled={!pages}
            >
              {pages &&
                pages.map((page) => (
                  <MenuItem
                    key={page.urlPath}
                    value={page.urlPath}
                    data-page-key={page.urlPath}
                    data-testid={pageListContentItemTestIds.pagePathMenuItem}
                  >
                    {page.urlPath}
                  </MenuItem>
                ))}
              {!pages && <MenuItem value=""></MenuItem>}
            </Select>
          </FormControl>
        </FormField>
        {treatment === UiModulePageListTreatment.CLIPS_VERTICAL_TILES && (
          <FormField>
            <Controller
              control={control}
              name={`pages.${index}.description`}
              render={({ field: { value, onChange } }) => (
                <LokaliseAutocomplete
                  label={t('layouts.page_list.fields.description')}
                  name="description"
                  value={value as string}
                  onChange={onChange}
                  required={false}
                  data-testid={pageListContentItemTestIds.descriptionInput}
                />
              )}
            />
          </FormField>
        )}
        {[UiModulePageListTreatment.CAROUSELS_LIST, UiModulePageListTreatment.CLIPS_VERTICAL_TILES].includes(
          treatment
        ) && (
          <FormField>
            <AssetPicker name="cardImage" assetType={AssetTypes.pageList.cardImage} />
          </FormField>
        )}
      </div>
      <div className={classes.rowRight}>
        <DragHandle className={`${classes.dragHandle} pageListDragHandle`} />
        <IconButton
          onClick={onDelete}
          title={t('layouts.hero_delete_content_item')}
          size="large"
          data-testid={pageListContentItemTestIds.deleteItemButton}
        >
          <Delete />
        </IconButton>
      </div>
    </div>
  );
}

export default PageListContentItem;
